/**
 * Pulcher UI helpers — reveals, header scroll class, modal wiring, size finder form.
 */

import { recommendSize } from '@theme/pulcher-size-finder';

function initReveals() {
  const nodes = document.querySelectorAll('.pulcher-reveal');
  if (!nodes.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    nodes.forEach((node) => node.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  nodes.forEach((node) => observer.observe(node));
}

function initHeaderScroll() {
  const header = document.querySelector('#header-component');
  if (!header) return;

  const onScroll = () => {
    const scrolled = window.scrollY > 24;
    header.classList.toggle('is-scrolled', scrolled);
    document.body.classList.toggle('pulcher-header-scrolled', scrolled);
  };

  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

function wireModalTriggers() {
  document.querySelectorAll('[data-pulcher-open]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const id = trigger.getAttribute('data-pulcher-open');
      const dialog = id ? document.getElementById(id) : null;
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
      }
    });
  });

  document.querySelectorAll('[data-pulcher-close]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dialog = btn.closest('dialog');
      if (dialog) dialog.close();
    });
  });

  document.querySelectorAll('dialog.pulcher-modal').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });
}

function initSizeFinderForms() {
  document.querySelectorAll('[data-pulcher-size-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const root = form.closest('[data-pulcher-size-finder]') || form.parentElement;
      const resultEl = root?.querySelector('[data-pulcher-size-result]');
      const sizeEl = root?.querySelector('[data-pulcher-size-value]');
      const reasonEl = root?.querySelector('[data-pulcher-size-reason]');
      const confidenceEl = root?.querySelector('[data-pulcher-size-confidence]');

      const data = new FormData(form);
      try {
        const result = recommendSize({
          gender: String(data.get('gender') || 'female'),
          heightCm: Number(data.get('height')),
          weightKg: Number(data.get('weight')),
          preferredFit: String(data.get('fit') || 'Regular'),
          rules: root?.querySelector('[data-pulcher-size-rules]')?.textContent?.trim() || null,
        });

        if (sizeEl) sizeEl.textContent = result.size;
        if (reasonEl) reasonEl.textContent = result.reason;
        if (confidenceEl) confidenceEl.textContent = `${result.confidence}% confidence`;
        resultEl?.classList.add('is-visible');

        // Optionally highlight matching variant option if present on PDP
        const option = document.querySelector(
          `variant-picker [data-value="${result.size}"], .variant-option[value="${result.size}"]`
        );
        if (option && typeof option.click === 'function') option.click();
      } catch (error) {
        if (reasonEl) reasonEl.textContent = error.message || 'Unable to recommend a size.';
        resultEl?.classList.add('is-visible');
      }
    });
  });
}

function initShippingProgress() {
  const root = document.querySelector('[data-pulcher-shipping-progress]');
  if (!root) return;

  const threshold = Number(root.getAttribute('data-threshold') || 0);
  const total = Number(root.getAttribute('data-cart-total') || 0);
  const bar = root.querySelector('[data-pulcher-shipping-bar]');
  const label = root.querySelector('[data-pulcher-shipping-label]');
  if (!threshold || !bar) return;

  const ratio = Math.min(1, total / threshold);
  bar.style.width = `${ratio * 100}%`;

  if (label) {
    if (total >= threshold) {
      label.textContent = 'You have unlocked free shipping';
    } else {
      const remaining = ((threshold - total) / 100).toFixed(2);
      label.textContent = `Spend ${remaining} more for free shipping`;
    }
  }
}

function boot() {
  initReveals();
  initHeaderScroll();
  wireModalTriggers();
  initSizeFinderForms();
  initShippingProgress();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
